import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Search, Star, Eye, ExternalLink, Calendar, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Globe, FolderTree, Layers, Trash2, Shield, Key, CheckCircle } from 'lucide-react'
import { SHARED_TEMPLATES, CATEGORIES, SCENARIOS, SCOPES, CLASSIFICATIONS, TAGS, INSTITUTION_TREE } from '@/data/shared-templates'
import type { SharedTemplate, InstitutionNode } from '@/data/shared-templates'
import type { UserBizRole } from '@/utils/user-role'
import { ROLE_CATEGORY_MAP } from '@/utils/user-role'

export function SharedTemplates({ onViewReport, showFavoritesOnly: initialShowFavorites, version, userBizRole }: { onViewReport?: (templateId?: string) => void; showFavoritesOnly?: boolean; version?: string; userBizRole?: UserBizRole }) {
  const [keyword, setKeyword] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [activeScope, setActiveScope] = useState<string>('all')
  const [activeClassification, setActiveClassification] = useState<string | null>(null)
  const [activeTags, setActiveTags] = useState<string[]>([])
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(initialShowFavorites ?? false)
  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionNode | null>(null)
  const [showInstitutionDialog, setShowInstitutionDialog] = useState(false)
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('template_favorites')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) return new Set(parsed)
      }
    } catch {
      // ignore
    }
    return new Set<string>()
  })

  // 用户从"我的空间"共享的报表
  const [userSharedTemplates, setUserSharedTemplates] = useState<SharedTemplate[]>(() => {
    try {
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      return (shared as any[]).map((s: any) => ({
        id: s.id,
        name: s.name,
        department: '金融科技部（数字化转型办公室）',
        description: s.description || '',
        createdAt: s.sharedAt || '',
        views: 0,
        favorites: 0,
        category: '其他',
        scenario: '营销推动',
        scope: 'mine',
      }))
    } catch {
      return []
    }
  })

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<SharedTemplate | null>(null)

  const handleDeleteTemplate = useCallback((template: SharedTemplate) => {
    setTemplateToDelete(template)
    setShowDeleteModal(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!templateToDelete) return
    try {
      const shared = JSON.parse(localStorage.getItem('shared_reports') || '[]')
      const updated = (shared as any[]).filter((s: any) => s.id !== templateToDelete.id)
      localStorage.setItem('shared_reports', JSON.stringify(updated))
      setUserSharedTemplates(prev => prev.filter(t => t.id !== templateToDelete.id))
    } catch {
      // ignore
    }
    setShowDeleteModal(false)
    setTemplateToDelete(null)
  }, [templateToDelete])

  useEffect(() => {
    localStorage.setItem('template_favorites', JSON.stringify([...favorites]))
  }, [favorites])

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  /** 记录一次访问 */
  const touchLastViewed = useCallback((id: string) => {
    try {
      const data = JSON.parse(localStorage.getItem('recently_viewed') || '{}')
      data[id] = Date.now()
      localStorage.setItem('recently_viewed', JSON.stringify(data))
    } catch { /* ignore */ }
  }, [])

  // 权限申请状态
  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [selectedPermissionTemplate, setSelectedPermissionTemplate] = useState<SharedTemplate | null>(null)
  const [permissionSubmitted, setPermissionSubmitted] = useState(false)

  /** 打开权限申请弹窗 */
  const handleApplyPermission = useCallback((template: SharedTemplate) => {
    setSelectedPermissionTemplate(template)
    setPermissionSubmitted(false)
    setShowPermissionModal(true)
  }, [])

  /** 提交权限申请 */
  const handlePermissionSubmit = useCallback((type: 'role' | 'template') => {
    if (!selectedPermissionTemplate) return
    try {
      const requests = JSON.parse(localStorage.getItem('permission_requests') || '[]')
      const newRequest = {
        id: `perm-${Date.now()}`,
        templateId: selectedPermissionTemplate.id,
        templateName: selectedPermissionTemplate.name,
        requiredCategory: selectedPermissionTemplate.category,
        type,
        status: 'pending',
        requestedAt: new Date().toISOString(),
      }
      requests.push(newRequest)
      localStorage.setItem('permission_requests', JSON.stringify(requests))
    } catch { /* ignore */ }
    setPermissionSubmitted(true)
  }, [selectedPermissionTemplate])

  // 合并内置模板和用户共享模板
  const allTemplates = [...SHARED_TEMPLATES, ...userSharedTemplates]

  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 10

  const favoriteCount = useMemo(
    () => allTemplates.filter(t => favorites.has(t.id)).length,
    [allTemplates, favorites]
  )

  const allowedCategory = userBizRole ? ROLE_CATEGORY_MAP[userBizRole] : null

  // 报表模板库业务筛选项仅保留零售业务、对公业务
  const TEMPLATE_FILTER_CATEGORIES = ['零售业务', '对公业务'] as const

  // 分类筛选项仅保留规模、拓户
  const TEMPLATE_FILTER_CLASSIFICATIONS = ['规模', '拓户'] as const

  const filtered = allTemplates.filter(t => {
    // 非匹配角色模板仍展示，但排在后方（不再硬过滤掉）
    if (showFavoritesOnly && !favorites.has(t.id)) return false
    if (keyword) {
      const k = keyword.toLowerCase()
      if (!t.name.toLowerCase().includes(k) && !t.description.toLowerCase().includes(k)) return false
    }
    if (activeCategory && t.category !== activeCategory) return false
    if (activeScenario && t.scenario !== activeScenario) return false
    if (activeClassification && t.classification !== activeClassification) return false
    if (activeTags.length > 0 && !activeTags.some(tag => t.tags?.includes(tag))) return false
    if (activeScope === 'mine' && t.scope !== 'mine') return false
    if (activeScope === 'headquarters') return false
    if (activeScope === 'branch' && t.scope !== 'branch') return false
    if (activeScope === 'subbranch') return false // 暂无网点专属模板
    if (activeScope === 'institution' && selectedInstitution) {
      // 按机构筛选模拟逻辑
      const instName = findInstitutionPath(selectedInstitution.id)
      if (!t.department.includes(instName) && t.scope !== 'all') return false
    }
    return true
  })

  // 排序：匹配当前角色业务的模板在前，非匹配的排在后方
  const sorted = [...filtered].sort((a, b) => {
    const aAllowed = !allowedCategory || a.category === allowedCategory
    const bAllowed = !allowedCategory || b.category === allowedCategory
    if (aAllowed && !bAllowed) return -1
    if (!aAllowed && bAllowed) return 1
    return 0
  })

  // 筛选条件变化时重置页码
  const filterKey = `${keyword}-${activeCategory}-${activeScenario}-${activeScope}-${selectedInstitution?.id}-${showFavoritesOnly}-${activeClassification}-${activeTags.join(',')}`
  useEffect(() => { setCurrentPage(1) }, [filterKey])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const startIndex = (safePage - 1) * PAGE_SIZE
  const pageData = sorted.slice(startIndex, startIndex + PAGE_SIZE)

  // 悬浮描述 tooltip — 在表格外层渲染，避免被 ScrollArea 裁剪
  const hoveredDescRef = useRef<{ text: string; rect: DOMRect } | null>(null)
  const [hoveredDescTick, setHoveredDescTick] = useState(0)

  const handleDescMouseEnter = (text: string, el: HTMLElement) => {
    hoveredDescRef.current = { text, rect: el.getBoundingClientRect() }
    setHoveredDescTick(n => n + 1)
  }

  const handleDescMouseLeave = () => {
    hoveredDescRef.current = null
    setHoveredDescTick(n => n + 1)
  }

  useEffect(() => {
    const onScroll = () => {
      if (hoveredDescRef.current) {
        hoveredDescRef.current = null
        setHoveredDescTick(n => n + 1)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleSelectInstitution = (inst: InstitutionNode) => {
    if (!inst.children) {
      setSelectedInstitution(inst)
      setShowInstitutionDialog(false)
    }
  }

  // 查找机构路径名
  function findInstitutionPath(targetId: string, nodes: InstitutionNode[] = INSTITUTION_TREE, path: string = ''): string {
    for (const node of nodes) {
      const currentPath = path ? `${path} / ${node.name}` : node.name
      if (node.id === targetId) return currentPath
      if (node.children) {
        const found = findInstitutionPath(targetId, node.children, currentPath)
        if (found) return found
      }
    }
    return ''
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-6">
        {/* 搜索框 */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="搜索模板名称、介绍..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="h-10 pl-10 pr-4 text-sm rounded-md border border-gray-300 focus:border-gcb-primary focus:outline-none focus:ring-1 focus:ring-gcb-primary"
            />
          </div>
        </div>

        {/* 筛选栏 */}
        <div className="bg-white rounded-lg border p-4 mb-4 space-y-3">
          {/* 范围 - v2.0 only */}
          {version !== '1' && (<div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <Globe className="w-3 h-3" /> 范围：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {SCOPES.map(s => (
                <FilterChip
                  key={s.value}
                  label={s.label}
                  active={activeScope === s.value}
                  onClick={() => {
                    setActiveScope(s.value)
                    if (s.value === 'institution') {
                      setShowInstitutionDialog(true)
                    } else {
                      setSelectedInstitution(null)
                    }
                  }}
                />
              ))}
              {activeScope === 'institution' && selectedInstitution && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-blue-50 text-gcb-primary border-gcb-primary/20">
                  {findInstitutionPath(selectedInstitution.id)}
                  <button
                    className="ml-1 text-gcb-primary hover:text-gcb-primaryDark"
                    onClick={() => { setSelectedInstitution(null); setActiveScope('all') }}
                  >
                    ×
                  </button>
                </Badge>
              )}
            </div>
          </div>)}
          {/* 业务 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <FolderTree className="w-3 h-3" /> 业务：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip label="全部" active={!activeCategory} onClick={() => setActiveCategory(null)} />
              {TEMPLATE_FILTER_CATEGORIES.map(cat => (
                <FilterChip key={cat} label={cat} active={activeCategory === cat} onClick={() => setActiveCategory(cat)} />
              ))}
            </div>
          </div>
          {/* 场景 */}
          {version !== '1' && (<div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <Layers className="w-3 h-3" /> 场景：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip label="全部" active={!activeScenario} onClick={() => setActiveScenario(null)} />
              {SCENARIOS.map(s => (
                <FilterChip key={s} label={s} active={activeScenario === s} onClick={() => setActiveScenario(s)} />
              ))}
            </div>
          </div>)}
          {/* 分类 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <FolderTree className="w-3 h-3" /> 分类：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip label="全部" active={!activeClassification} onClick={() => setActiveClassification(null)} />
              {TEMPLATE_FILTER_CLASSIFICATIONS.map(c => (
                <FilterChip key={c} label={c} active={activeClassification === c} onClick={() => setActiveClassification(c)} />
              ))}
            </div>
          </div>
          {/* 标签 + 我收藏的 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <Star className="w-3 h-3" /> 标签：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {TAGS.map(tag => (
                <FilterChip key={tag} label={tag} active={activeTags.includes(tag)} onClick={() => {
                  setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
                }} />
              ))}
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`px-2.5 py-1 rounded-md text-xs transition-all flex items-center gap-1 ${
                  showFavoritesOnly
                    ? 'bg-gcb-primary text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Star className={`w-3 h-3 ${showFavoritesOnly ? 'fill-white' : ''}`} />
                我收藏的
              </button>
              {(activeTags.length > 0 || showFavoritesOnly) && (
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 ml-1"
                  onClick={() => { setActiveTags([]); setShowFavoritesOnly(false) }}
                >
                  清空
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 统计信息 */}

        {/* 模板列表 */}
        <div className="relative">
          <CardTable templates={pageData} favorites={favorites} onToggleFavorite={toggleFavorite} onViewReport={onViewReport} onDeleteTemplate={handleDeleteTemplate} onTouchLastViewed={touchLastViewed} allowedCategory={allowedCategory} onApplyPermission={handleApplyPermission} onDescMouseEnter={handleDescMouseEnter} onDescMouseLeave={handleDescMouseLeave} />

          {/* 悬浮描述 tooltip（固定定位，不受滚动区域裁剪） */}
          {hoveredDescRef.current && (
            <div
              className="fixed z-[9999] max-w-[400px] px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg pointer-events-none whitespace-normal"
              style={{
                left: `${hoveredDescRef.current.rect.left}px`,
                top: `${hoveredDescRef.current.rect.top - 8}px`,
                transform: 'translateY(-100%)',
              }}
            >
              {hoveredDescRef.current.text}
            </div>
          )}
        </div>

        {/* 分页 */}
        {sorted.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 py-3">
            <div className="text-xs text-gray-500">
              第 <span className="font-medium text-gray-900">{safePage}</span> / {totalPages} 页，共 {sorted.length} 条
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage <= 1} onClick={() => setCurrentPage(1)}>
                <ChevronsLeft className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <Button
                  key={p}
                  variant={safePage === p ? 'default' : 'outline'}
                  size="sm"
                  className={`h-7 w-7 p-0 text-xs ${safePage === p ? 'bg-gcb-primary text-white' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </Button>
              ))}
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
                <ChevronsRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 机构选择弹窗 */}
      <Dialog open={showInstitutionDialog} onOpenChange={setShowInstitutionDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>选择机构</DialogTitle>
            <DialogDescription>请选择需要筛选的分支机构</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <InstitutionTree
              nodes={INSTITUTION_TREE}
              selectedId={selectedInstitution?.id}
              onSelect={handleSelectInstitution}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>删除共享模板</DialogTitle>
            <DialogDescription>
              删除后将无法恢复，请谨慎操作。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-700">
              确定要删除"<span className="font-medium text-gray-900">{templateToDelete?.name}</span>"吗？
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="h-10 px-4">
              取消
            </Button>
            <Button onClick={handleConfirmDelete} className="h-10 px-6 bg-red-600 hover:bg-red-700">
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 申请权限弹窗 */}
      <Dialog open={showPermissionModal} onOpenChange={setShowPermissionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>申请模板权限</DialogTitle>
            <DialogDescription>
              "{selectedPermissionTemplate?.name}" 属于 {selectedPermissionTemplate?.category}，需要相应角色权限才能查看。
            </DialogDescription>
          </DialogHeader>
          {permissionSubmitted ? (
            <div className="py-6 flex flex-col items-center text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">申请已提交</p>
              <p className="text-xs text-gray-500">您的权限申请已提交给管理员，预计 1-2 个工作日内处理。</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3">
                <p className="font-medium mb-2 flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-gcb-primary" />
                  请选择申请方式：
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handlePermissionSubmit('role')}
                    className="w-full text-left p-2.5 rounded-lg border border-gray-200 hover:border-gcb-primary hover:bg-blue-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900">申请「{selectedPermissionTemplate?.category}」角色权限</p>
                    <p className="text-xs text-gray-500 mt-0.5">获得该角色下所有模板的查看权限</p>
                  </button>
                  <button
                    onClick={() => handlePermissionSubmit('template')}
                    className="w-full text-left p-2.5 rounded-lg border border-gray-200 hover:border-gcb-primary hover:bg-blue-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900">仅申请本模板权限</p>
                    <p className="text-xs text-gray-500 mt-0.5">仅获得「{selectedPermissionTemplate?.name}」的查看权限</p>
                  </button>
                </div>
              </div>
              <div className="text-xs text-gray-500 bg-gray-50 rounded-md p-3">
                <p className="font-medium mb-1">审批流程：</p>
                <p>1. 部门负责人审批 → 2. 管理员审批 → 3. 授予权限</p>
                <p className="mt-1">预计审批时间：1-2 个工作日</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPermissionModal(false)}
              className="h-10 px-4"
            >
              {permissionSubmitted ? '关闭' : '取消'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ---- Filter Chip ---- */
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-xs transition-all ${
        active
          ? 'bg-gcb-primary text-white shadow-sm'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  )
}

/* ---- Institution Tree ---- */
function InstitutionTree({
  nodes,
  selectedId,
  onSelect,
  level = 0,
}: {
  nodes: InstitutionNode[]
  selectedId?: string
  onSelect: (node: InstitutionNode) => void
  level?: number
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['hq']))

  const toggleExpand = (id: string, hasChildren: boolean) => {
    if (!hasChildren) return
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-1">
      {nodes.map(node => {
        const isExpanded = expandedIds.has(node.id)
        const isSelected = selectedId === node.id
        const hasChildren = !!(node.children && node.children.length > 0)

        return (
          <div key={node.id}>
            <button
              className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm transition-colors text-left ${
                isSelected
                  ? 'bg-blue-100 text-gcb-primaryDark font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ paddingLeft: `${level * 16 + 8}px` }}
              onClick={() => {
                toggleExpand(node.id, hasChildren)
                onSelect(node)
              }}
            >
              {hasChildren ? (
                <ChevronRight
                  className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <span className={hasChildren ? 'font-medium' : ''}>{node.name}</span>
            </button>
            {hasChildren && isExpanded && (
              <InstitutionTree
                nodes={node.children!}
                selectedId={selectedId}
                onSelect={onSelect}
                level={level + 1}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ---- Card Table ---- */
function CardTable({
  templates,
  favorites,
  onToggleFavorite,
  onViewReport,
  onDeleteTemplate,
  onTouchLastViewed,
  allowedCategory,
  onApplyPermission,
  onDescMouseEnter,
  onDescMouseLeave,
}: {
  templates: SharedTemplate[]
  favorites: Set<string>
  onToggleFavorite: (id: string) => void
  onViewReport?: (templateId?: string) => void
  onDeleteTemplate?: (template: SharedTemplate) => void
  onTouchLastViewed?: (id: string) => void
  allowedCategory?: string | null
  onApplyPermission?: (template: SharedTemplate) => void
  onDescMouseEnter?: (text: string, el: HTMLElement) => void
  onDescMouseLeave?: () => void
}) {
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* 表头 */}
      <div className="h-10 bg-gray-50 border-b grid grid-cols-12 items-center px-4 text-xs font-medium text-gray-500">
        <div className="col-span-3 min-w-0">模板名称</div>
        <div className="col-span-1 text-center whitespace-nowrap">业务</div>
        <div className="col-span-1 text-center whitespace-nowrap">分类</div>
        <div className="col-span-1 text-center whitespace-nowrap">标签</div>
        <div className="col-span-3 min-w-0">模板介绍</div>
        <div className="col-span-1 text-center whitespace-nowrap">点击量</div>
        <div className="col-span-1 text-center whitespace-nowrap">收藏量</div>
        <div className="col-span-1 text-center">操作</div>
      </div>

      {/* 列表 */}
      <ScrollArea className="h-[420px]">
        {templates.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">暂无匹配的模板</div>
        ) : (
          templates.map(t => {
            const isUserShared = t.scope === 'mine'
            return (
            <div
              key={t.id}
              className="grid grid-cols-12 items-center px-4 py-3 border-b border-gray-50 hover:bg-blue-50/30 transition-colors group text-sm"
            >
              {/* 名称 */}
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shrink-0">
                  <ExternalLink className="w-3.5 h-3.5 text-gcb-primary" />
                </div>
                <span className="font-medium text-gray-900 truncate">{t.name}</span>
              </div>
              {/* 业务 */}
              <div className="col-span-1 text-center pr-2">
                <Badge variant="outline" className="text-xs h-5 px-2">{t.category}</Badge>
              </div>
              {/* 分类 */}
              <div className="col-span-1 text-center pr-2">
                <Badge className="text-xs h-5 px-2 bg-purple-50 text-purple-700 border-purple-200">{t.classification || '—'}</Badge>
              </div>
              {/* 标签 */}
              <div className="col-span-1 text-center pr-2">
                {t.tags && t.tags.length > 0 ? (
                  <div className="flex flex-wrap justify-center gap-0.5">
                    {t.tags.map(tag => (
                      <Badge key={tag} className="text-xs h-5 px-1.5 bg-amber-50 text-amber-700 border-amber-200">{tag}</Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </div>
              {/* 介绍 */}
              <div
                className="col-span-3 min-w-0 pr-2"
                onMouseEnter={e => onDescMouseEnter?.(t.description, e.currentTarget)}
                onMouseLeave={() => onDescMouseLeave?.()}
              >
                <span className="text-gray-500 text-xs truncate block">{t.description}</span>
              </div>
              {/* 点击量 */}
              <div className="col-span-1 text-center text-gray-500 text-xs flex items-center justify-center gap-1">
                <Eye className="w-3 h-3" />
                {t.views}
              </div>
              {/* 收藏量 */}
              <div className="col-span-1 text-center text-gray-500 text-xs flex items-center justify-center gap-1">
                <Star className="w-3 h-3" />
                {t.favorites + (favorites.has(t.id) ? 1 : 0)}
              </div>
              {/* 操作 */}
              <div className="col-span-1 flex items-center justify-center gap-1">
                {(() => {
                  const isAllowed = !allowedCategory || t.category === allowedCategory
                  if (isAllowed) {
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-gcb-primary hover:text-gcb-primaryDark hover:bg-blue-50"
                        onClick={() => { onTouchLastViewed?.(t.id); onViewReport?.(t.id) }}
                        title="查看"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )
                  }
                  // 非匹配角色模板：显示申请权限图标
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                      onClick={() => onApplyPermission?.(t)}
                      title="申请权限"
                    >
                      <Shield className="w-4 h-4" />
                    </Button>
                  )
                })()}
                <button
                  onClick={() => onToggleFavorite(t.id)}
                  className={`transition-colors ${
                    favorites.has(t.id)
                      ? 'text-yellow-500 hover:text-yellow-600'
                      : 'text-gray-300 hover:text-yellow-400'
                  }`}
                  title={favorites.has(t.id) ? '取消收藏' : '收藏'}
                >
                  <Star className={`w-4 h-4 ${favorites.has(t.id) ? 'fill-yellow-500' : ''}`} />
                </button>
                {isUserShared && onDeleteTemplate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => onDeleteTemplate(t)}
                  title="删除共享模板"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                )}
              </div>
            </div>
            )
          })
        )}
      </ScrollArea>
    </div>
  )
}
